import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { resolveSite } from '@/lib/site'
import { copy } from './Slide7FollowBreadcrumbs.copy'

const ease = [0.16, 1, 0.3, 1] as const
const PAYPAL_MARK_URL = 'https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg'

const HOME_APPS = [
  { id: 'paypal', highlight: true },
  { id: 'mail', highlight: false },
  { id: 'shop', highlight: false },
  { id: 'maps', highlight: false },
  { id: 'photos', highlight: false },
  { id: 'settings', highlight: false },
]

function PayPalMark({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <img src={PAYPAL_MARK_URL} alt="" className={className} />
  )
}

/** Neutral bank mark for the Qatar face: the mockup impersonates "your bank"
 *  rather than PayPal, and no real bank brand is shown. */
function BankMark({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M9.674 2.075a.75.75 0 01.652 0l7.25 3.5A.75.75 0 0117 6.95v.55a.75.75 0 01-.75.75H3.75A.75.75 0 013 7.5v-.55a.75.75 0 01.424-.675l7.25-3.5zM5 9.5h1.5v5H5v-5zm4.25 0h1.5v5h-1.5v-5zm4.25 0H15v5h-1.5v-5zM3.5 16a.75.75 0 01.75-.75h11.5a.75.75 0 010 1.5H4.25A.75.75 0 013.5 16z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export default function Slide7FollowBreadcrumbs({ isActive }: SlideProps) {
  const c = useSlideCopy(copy)
  const isAr = useLocale() === 'ar'
  // The SMS-to-app diagram is drawn in physical LTR coordinates (SVG arrow
  // plus absolute offsets), then mirrored as a whole under RTL via
  // `dir-flip`. Text-bearing children carry `dir-flip` again to read normally.
  const textDir = isAr ? 'rtl' : 'ltr'
  const offscreen = isAr ? '-100%' : '100%'
  const isQatar = resolveSite() === 'qatar'
  const Mark = isQatar ? BankMark : PayPalMark
  const markTint = isQatar ? 'bg-violet-50 text-violet-700' : 'bg-[#e8f5fd]'
  const appTint = isQatar
    ? 'bg-violet-50 text-violet-700 shadow-[0_10px_20px_rgba(15,23,42,0.12)] ring-1 ring-violet-200'
    : 'bg-[#e8f5fd] text-white shadow-[0_10px_20px_rgba(23,155,215,0.18)] ring-1 ring-[#b7e2f7]'
  const [guideOpen, setGuideOpen] = useState(false)

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.05, ease }}
        className="mb-8 w-full max-w-[720px]"
      >
        <div dir="ltr" className="dir-flip relative h-[206px] overflow-hidden rounded-[30px] bg-white">
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={isActive ? { opacity: 1, x: 0 } : { opacity: 0, x: -18 }}
            transition={{ duration: 0.6, delay: 0.25, ease }}
            className="absolute start-[42px] top-[42px] w-[214px] rounded-[22px] border border-slate-200 bg-white px-4 py-3 text-start"
          >
            <div dir={textDir} className="dir-flip">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${markTint}`}>
                    <Mark className="h-4.5 w-4.5" />
                  </span>
                  <div>
                    <p className="text-[12px] font-semibold text-slate-800">
                      {c.senderName}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {c.senderChannel}
                    </p>
                  </div>
                </div>
                <span className="text-[11px] text-slate-400">
                  {c.now}
                </span>
              </div>
              <p className="mt-2.5 text-[13px] leading-snug text-slate-600">
                {c.smsBefore}
                <span className="text-sky-600 underline decoration-sky-300 underline-offset-2">
                  {c.smsLink}
                </span>
                {c.smsAfter}
              </p>
            </div>
          </motion.div>

          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 720 206" fill="none" aria-hidden="true">
            <defs>
              <marker
                id="official-app-arrow"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0 1.5L6 4L0 6.5V1.5Z" fill="#94a3b8" />
              </marker>
            </defs>
            <motion.line
              x1="276"
              y1="106"
              x2="276"
              y2="106"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="8 8"
              strokeLinecap="round"
              markerEnd="url(#official-app-arrow)"
              initial={{ opacity: 0 }}
              animate={isActive ? { x2: 388, opacity: 1 } : { x2: 276, opacity: 0 }}
              transition={{ duration: 0.72, delay: 1.05, ease }}
            />
          </svg>

          <motion.div
            initial={{ opacity: 0, x: 18, y: 8, scale: 0.98 }}
            animate={isActive ? { opacity: 1, x: 0, y: 0, scale: 1 } : { opacity: 0, x: 18, y: 8, scale: 0.98 }}
            transition={{ duration: 0.55, delay: 1.88, ease }}
            className="absolute end-[58px] top-[10px] h-[244px] w-[270px] overflow-hidden rounded-[40px] bg-[#10182b] p-[4px] shadow-[0_18px_40px_rgba(15,23,42,0.14)] ring-1 ring-slate-900/10"
          >
            <div dir={textDir} className="dir-flip relative h-full rounded-[34px] bg-[#f4f5f7] ring-1 ring-white/80">
              <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-2 text-[8px] font-medium text-slate-500">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <svg className="h-[7px] w-[10px]" viewBox="0 0 10 7" fill="none" aria-hidden="true">
                    <rect x="0.75" y="1" width="6.2" height="5" rx="1" stroke="currentColor" strokeWidth="0.9" />
                    <path d="M7.6 2.4V4.6" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
                    <rect x="1.7" y="1.95" width="4.1" height="3.1" rx="0.55" fill="currentColor" />
                  </svg>
                  <svg className="h-[7px] w-[8px]" viewBox="0 0 8 7" fill="currentColor" aria-hidden="true">
                    <rect x="0.5" y="4.5" width="1.1" height="2" rx="0.35" />
                    <rect x="2.7" y="3.3" width="1.1" height="3.2" rx="0.35" />
                    <rect x="4.9" y="2.1" width="1.1" height="4.4" rx="0.35" />
                  </svg>
                </div>
              </div>

              <div className="absolute inset-x-0 top-0 flex justify-center pt-2.5">
                <div className="h-[8px] w-[78px] rounded-full bg-[#0f172a]" />
              </div>

              <div className="px-5 pt-10">
                <div className="grid grid-cols-3 gap-x-4 gap-y-5">
                  {HOME_APPS.map((app, index) => (
                    <div key={app.id} className="flex flex-col items-center gap-2">
                      <div className="relative">
                        {app.highlight && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={isActive ? { opacity: [0.2, 0.48, 0.2], scale: [1, 1.08, 1] } : { opacity: 0, scale: 0.9 }}
                            transition={{ duration: 2.4, repeat: Infinity, delay: 2.35, ease }}
                            className="absolute inset-[-6px] rounded-[20px] border border-violet-300/80"
                          />
                        )}
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-[16px] ${
                            app.highlight
                              ? appTint
                              : 'bg-white text-slate-400 ring-1 ring-slate-200/80'
                          }`}
                          style={{ opacity: app.highlight ? 1 : 1 - index * 0.05 }}
                        >
                          {app.highlight ? (
                            <Mark />
                          ) : (
                            <div className="h-5 w-5 rounded-[6px] bg-slate-200" />
                          )}
                        </div>
                      </div>
                      <div className={`h-1.5 rounded-full ${app.highlight ? 'w-8 bg-slate-400/80' : 'w-7 bg-slate-300/80'}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15, ease }}
        className="text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] text-balance text-center max-w-3xl"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-3xl text-center"
      >
        {c.body}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.82, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.guideButton}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </motion.button>

      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-s border-slate-200 bg-white text-start shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M2 5.5A1.5 1.5 0 013.5 4h13A1.5 1.5 0 0118 5.5v9a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 012 14.5v-9zm1.75.125L10 10.25l6.25-4.625V5.5a.25.25 0 00-.25-.25h-12a.25.25 0 00-.25.25v.125z" />
                      </svg>
                    </span>
                    <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                      {c.guideTitle}
                    </h3>
                  </div>
                  <button
                    onClick={() => setGuideOpen(false)}
                    aria-label={c.closeAria}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <p className="mb-7 text-base leading-relaxed text-slate-600">
                  {c.intro}
                </p>
                <div className="space-y-7">
                  <section>
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.bankHeading}
                    </h4>
                    <p className="mt-1 text-base leading-relaxed text-slate-600">
                      {c.bankBody}
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.deliveryHeading}
                    </h4>
                    <p className="mt-1 text-base leading-relaxed text-slate-600">
                      {c.deliveryBody}
                    </p>
                  </section>

                  <section>
                    <div className="flex items-center gap-2.5">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M10 1a6 6 0 00-3.815 10.631C7.237 12.5 8 13.443 8 14.456v.644a.75.75 0 00.572.729 6.016 6.016 0 002.856 0A.75.75 0 0012 15.1v-.644c0-1.013.762-1.957 1.815-2.825A6 6 0 0010 1z" />
                          <path fillRule="evenodd" d="M7.75 17.5h4.5a.75.75 0 000-1.5h-4.5a.75.75 0 000 1.5zm.75 2a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <h4 className="text-lg font-semibold text-slate-900">
                        {c.tipsHeading}
                      </h4>
                    </div>
                    <ul className="mt-3 space-y-2.5 text-base leading-relaxed text-slate-600">
                      {c.tips.map((tip) => (
                        <li key={tip} className="flex gap-2.5">
                          <svg className="mt-[5px] h-4 w-4 flex-shrink-0 text-emerald-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
